/*
 * Final cart checkout guard.
 *
 * Blocks checkout until the cart has a valid shipping option, estimated ship
 * date, and exactly one matching marker SKU. Reuses KSShippingGate /
 * KSShippingOption. Does not intercept homepage BYOB add-to-cart.
 *
 * Shop Pay / express checkout: NOT disabled globally. Pre-checkout express
 * buttons are hidden only while shipping data is missing, then re-enabled.
 * Shop Pay remains usable inside Shopify checkout after a valid cart arrives.
 *
 * Public API:
 *   window.KSShippingCheckoutGuard.validateBeforeCheckout(event)
 *   window.KSShippingCheckoutGuard.cartRequiresShipping(cart)
 *   window.KSShippingCheckoutGuard.isCartValid(cart)
 *   window.KSShippingCheckoutGuard.getCartValidity(cart)
 *   window.KSShippingCheckoutGuard.patchCartLines(...)
 *   window.KSShippingCheckoutGuard.repairCartAfterSelection(shippingValue)
 *   window.KSShippingCheckoutGuard.syncCartExpressVisibility(cart?)
 *   window.KSShippingCheckoutGuard.ensureFormShippingProperties(form)
 */
(function () {
    "use strict";

    var BOUND_ATTR = "data-ks-shipping-checkout-guard-bound";
    var VALIDATED_ATTR = "data-ks-checkout-shipping-validated";
    var MARKER_SKUS = ["SH-NOW", "SH-1WK", "SH-2WK", "SH-3WK"];
    var MARKER_PROPERTY_KEY = "_shipping_option_marker";
    var ATTRIBUTE_KEY = "Shipping Option";
    var ESTIMATED_DATE_KEY = "Estimated Ship Date";
    var SUCCESS_MESSAGE =
        "Shipping option saved. You can continue to checkout.";
    var PENDING_ATTR = "data-ks-shipping-pending";
    var HIDE_EXPRESS_ATTR = "data-ks-hide-express";
    var CART_EXPRESS_BLOCKED_ATTR = "data-ks-cart-express-blocked";
    var SUCCESS_TOAST_ID = "ks-shipping-checkout-toast";
    var PRODUCT_EXPRESS_SELECTOR =
        ".shopify-payment-button, shopify-accelerated-checkout, .shopify-accelerated-checkout";

    var _busy = false;
    var _repairChain = Promise.resolve();

    function getKS() {
        return window.KSShippingOption || null;
    }

    function getGate() {
        return window.KSShippingGate || null;
    }

    function isDebug() {
        try {
            return (
                /(?:\?|&)ks_shipping_debug=1(?:&|$)/.test(
                    window.location.search || ""
                ) ||
                window.KS_SHIPPING_DEBUG === true
            );
        } catch (_) {
            return false;
        }
    }

    function debugLog() {
        if (!isDebug()) return;
        var args = Array.prototype.slice.call(arguments);
        args.unshift("[KSShippingCheckoutGuard]");
        try {
            console.log.apply(console, args);
        } catch (_) {
            /* ignore */
        }
    }

    function shopRoot() {
        var root =
            (window.Shopify &&
                window.Shopify.routes &&
                window.Shopify.routes.root) ||
            "/";
        return String(root).replace(/\/$/, "") || "";
    }

    function withRoot(path) {
        var clean = String(path || "").replace(/^\//, "");
        var root = shopRoot();
        return (root ? root + "/" : "/") + clean;
    }

    function getCartJsonUrl() {
        var routes = window.routes || {};
        if (routes.cart_url) {
            return String(routes.cart_url).replace(/\/$/, "") + ".js";
        }
        return withRoot("cart.js");
    }

    function getCartChangeUrl() {
        var routes = window.routes || {};
        if (routes.cart_change_url) return routes.cart_change_url;
        return withRoot("cart/change.js");
    }

    function getCartUrl() {
        var routes = window.routes || {};
        if (routes.cart_url) return routes.cart_url;
        return withRoot("cart");
    }

    function getCheckoutUrl() {
        return withRoot("checkout");
    }

    function postJson(url, body) {
        return fetch(url, {
            method: "POST",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify(body),
        });
    }

    function fetchCartJson() {
        return fetch(getCartJsonUrl(), {
            method: "GET",
            credentials: "same-origin",
            headers: { Accept: "application/json" },
        }).then(function (res) {
            if (!res.ok) throw new Error("cart.js failed: " + res.status);
            return res.json();
        });
    }

    function normalizeOption(value) {
        var KS = getKS();
        if (KS && typeof KS.normalizeShippingOption === "function") {
            return KS.normalizeShippingOption(value || "");
        }
        var raw = String(value || "").trim();
        if (raw === "Ship on next available ship day") return "Ship NOW";
        return raw;
    }

    function isValidOption(value) {
        var KS = getKS();
        var normalized = normalizeOption(value);
        if (KS && typeof KS.isValidShippingOption === "function") {
            return KS.isValidShippingOption(normalized);
        }
        return (
            normalized === "Ship NOW" ||
            normalized === "Hold 1 week" ||
            normalized === "Hold 2 weeks" ||
            normalized === "Hold 3 weeks"
        );
    }

    function getEstimatedForOption(value) {
        var KS = getKS();
        if (KS && typeof KS.getEstimatedShipDateForOption === "function") {
            return KS.getEstimatedShipDateForOption(normalizeOption(value));
        }
        return "";
    }

    function getMarkerPropertyKey() {
        var KS = getKS();
        return (KS && KS.MARKER_PROPERTY_KEY) || MARKER_PROPERTY_KEY;
    }

    function getMarkerSkus() {
        return MARKER_SKUS.slice();
    }

    function getAllMarkerVariantIds() {
        var markers = window.KS_SHIPPING_MARKERS;
        var ids = [];
        if (!markers || typeof markers !== "object") return ids;
        Object.keys(markers).forEach(function (key) {
            var m = markers[key];
            if (m && m.variant_id) ids.push(Number(m.variant_id));
        });
        return ids;
    }

    function getExpectedMarkerSku(optionValue) {
        var KS = getKS();
        var normalized = normalizeOption(optionValue);
        if (KS && typeof KS.getMarkerForValue === "function") {
            var marker = KS.getMarkerForValue(normalized);
            if (marker && marker.sku) return String(marker.sku).toUpperCase();
        }
        var map = {
            "Ship NOW": "SH-NOW",
            "Hold 1 week": "SH-1WK",
            "Hold 2 weeks": "SH-2WK",
            "Hold 3 weeks": "SH-3WK",
        };
        return map[normalized] || "";
    }

    function getExpectedMarkerVariantId(optionValue) {
        var KS = getKS();
        var normalized = normalizeOption(optionValue);
        if (KS && typeof KS.getMarkerForValue === "function") {
            var marker = KS.getMarkerForValue(normalized);
            if (marker && marker.variant_id) return Number(marker.variant_id);
        }
        var markers = window.KS_SHIPPING_MARKERS || {};
        var entry = markers[normalized];
        return entry && entry.variant_id ? Number(entry.variant_id) : null;
    }

    function itemSku(item) {
        return String((item && item.sku) || "").trim().toUpperCase();
    }

    function itemProps(item) {
        return (item && item.properties) || {};
    }

    function isMarkerLine(item) {
        if (!item) return false;
        var props = itemProps(item);
        var markerKey = getMarkerPropertyKey();
        if (props[markerKey] === "true" || props[markerKey] === true) {
            return true;
        }
        var sku = itemSku(item);
        if (sku && getMarkerSkus().indexOf(sku) !== -1) return true;
        var markerIds = getAllMarkerVariantIds();
        if (markerIds.indexOf(Number(item.variant_id)) !== -1) return true;
        return false;
    }

    function isGiftCardLine(item) {
        if (!item) return false;
        if (item.gift_card === true) return true;
        var type = String(item.product_type || "").toLowerCase();
        if (type.indexOf("gift card") !== -1 || type === "giftcard") {
            return true;
        }
        var handle = String(item.handle || "").toLowerCase();
        if (handle.indexOf("gift-card") !== -1) return true;
        return false;
    }

    function isUtilityNonShippingLine(item) {
        if (!item) return false;
        if (item.requires_shipping === false && !isMarkerLine(item)) {
            // Digital / non-shippable utility (gift cards already covered).
            if (isGiftCardLine(item)) return true;
            var props = itemProps(item);
            if (props._ks_non_shipping === "true" || props._ks_non_shipping === true) {
                return true;
            }
        }
        return isGiftCardLine(item);
    }

    function isRealShippingItem(item) {
        if (!item || Number(item.quantity) <= 0) return false;
        if (isMarkerLine(item)) return false;
        if (isUtilityNonShippingLine(item)) return false;
        return true;
    }

    function getRealItems(cart) {
        return ((cart && cart.items) || []).filter(isRealShippingItem);
    }

    function cartRequiresShipping(cart) {
        return getRealItems(cart).length > 0;
    }

    function readShippingOptionFromCart(cart) {
        var attrs = (cart && cart.attributes) || {};
        var fromAttr = normalizeOption(attrs[ATTRIBUTE_KEY] || "");
        if (isValidOption(fromAttr)) return fromAttr;

        var items = getRealItems(cart);
        for (var i = 0; i < items.length; i++) {
            var props = itemProps(items[i]);
            var fromLine = normalizeOption(props[ATTRIBUTE_KEY] || "");
            if (isValidOption(fromLine)) return fromLine;
        }
        return "";
    }

    function readEstimatedDateFromCart(cart, optionValue) {
        var attrs = (cart && cart.attributes) || {};
        var fromAttr = String(attrs[ESTIMATED_DATE_KEY] || "").trim();
        if (fromAttr) return fromAttr;

        var items = getRealItems(cart);
        for (var i = 0; i < items.length; i++) {
            var props = itemProps(items[i]);
            var fromLine = String(props[ESTIMATED_DATE_KEY] || "").trim();
            if (fromLine) return fromLine;
        }

        if (optionValue) return getEstimatedForOption(optionValue);
        return "";
    }

    function getMarkerLines(cart) {
        return ((cart && cart.items) || []).filter(isMarkerLine);
    }

    function getMatchingMarkerLines(cart, optionValue) {
        var expectedSku = getExpectedMarkerSku(optionValue);
        var expectedVariantId = getExpectedMarkerVariantId(optionValue);
        return getMarkerLines(cart).filter(function (item) {
            if (expectedSku && itemSku(item) === expectedSku) return true;
            if (
                expectedVariantId !== null &&
                Number(item.variant_id) === expectedVariantId
            ) {
                return true;
            }
            return false;
        });
    }

    function cartNoteHasShippingData(note) {
        var KS = getKS();
        if (KS && typeof KS.cartNoteHasShippingData === "function") {
            return KS.cartNoteHasShippingData(note);
        }
        var normalized = String(note || "")
            .replace(/<br\s*\/?>/gi, "\n")
            .replace(/\r\n/g, "\n");
        return (
            /Shipping Option\s*:\s*\S+/i.test(normalized) &&
            /Estimated Ship Date\s*:\s*\S+/i.test(normalized)
        );
    }

    function getCartValidity(cart) {
        var requires = cartRequiresShipping(cart);
        var result = {
            requiresShipping: requires,
            valid: true,
            shippingOption: "",
            estimatedShipDate: "",
            hasShippingOption: false,
            hasEstimatedShipDate: false,
            hasMatchingMarker: false,
            hasCartNote: false,
            markerOk: true,
            missingShippingOption: false,
            missingEstimatedShipDate: false,
            missingMarkerSku: false,
            missingCartNote: false,
            duplicateMarkers: false,
            wrongMarkers: false,
            reasons: [],
        };

        if (!requires) {
            return result;
        }

        var option = readShippingOptionFromCart(cart);
        var estimated = readEstimatedDateFromCart(cart, option);
        result.shippingOption = option;
        result.estimatedShipDate = estimated;
        result.hasShippingOption = isValidOption(option);
        result.hasEstimatedShipDate = Boolean(String(estimated || "").trim());
        result.hasCartNote = cartNoteHasShippingData(cart && cart.note);

        if (!result.hasShippingOption) {
            result.missingShippingOption = true;
            result.reasons.push("missing-shipping-option");
        }
        if (!result.hasEstimatedShipDate) {
            result.missingEstimatedShipDate = true;
            result.reasons.push("missing-estimated-ship-date");
        }
        if (!result.hasCartNote) {
            result.missingCartNote = true;
            result.reasons.push("missing-cart-note");
        }

        var allMarkers = getMarkerLines(cart);
        var matching = result.hasShippingOption
            ? getMatchingMarkerLines(cart, option)
            : [];
        var matchingQty = matching.reduce(function (sum, item) {
            return sum + Number(item.quantity || 0);
        }, 0);
        var wrong = allMarkers.filter(function (item) {
            return matching.indexOf(item) === -1;
        });

        result.wrongMarkers = wrong.length > 0;
        result.duplicateMarkers =
            matching.length > 1 || matchingQty > 1;
        result.hasMatchingMarker =
            matching.length === 1 && matchingQty === 1 && wrong.length === 0;
        result.markerOk = result.hasMatchingMarker;

        if (!result.hasMatchingMarker) {
            if (matching.length === 0 || matchingQty < 1) {
                result.missingMarkerSku = true;
                result.reasons.push("missing-marker-sku");
            }
            if (result.duplicateMarkers) {
                result.reasons.push("duplicate-markers");
            }
            if (result.wrongMarkers) {
                result.reasons.push("wrong-markers");
            }
        }

        result.valid =
            result.hasShippingOption &&
            result.hasEstimatedShipDate &&
            result.markerOk &&
            result.hasCartNote;

        return result;
    }

    function isCartValid(cart) {
        return getCartValidity(cart).valid;
    }

    function copyProperties(properties) {
        var out = {};
        var src = properties || {};
        Object.keys(src).forEach(function (key) {
            if (src[key] === null || typeof src[key] === "undefined") return;
            // Shopify cart.js may include a numeric "length" on property objects
            // from older themes; skip non-own array-ish length if present as only key.
            out[key] = src[key];
        });
        return out;
    }

    function lineNeedsShippingPatch(item, shippingOption, estimatedShipDate) {
        if (!isRealShippingItem(item)) return false;
        var props = itemProps(item);
        var lineOption = normalizeOption(props[ATTRIBUTE_KEY] || "");
        var lineDate = String(props[ESTIMATED_DATE_KEY] || "").trim();
        if (!isValidOption(lineOption)) return true;
        if (lineOption !== normalizeOption(shippingOption)) return true;
        if (!lineDate) return true;
        if (
            estimatedShipDate &&
            lineDate !== String(estimatedShipDate).trim()
        ) {
            return true;
        }
        return false;
    }

    function changeLine(body) {
        return postJson(getCartChangeUrl(), body).then(function (res) {
            if (!res.ok) {
                return Promise.reject(
                    new Error("cart/change failed: " + res.status)
                );
            }
            return res.json().catch(function () {
                return null;
            });
        });
    }

    function patchCartLines(cart, shippingOption, estimatedShipDate) {
        var normalized = normalizeOption(shippingOption);
        var estimated =
            String(estimatedShipDate || "").trim() ||
            getEstimatedForOption(normalized);
        var items = getRealItems(cart);
        var patchedKeys = [];

        var chain = Promise.resolve();
        items.forEach(function (item) {
            if (!lineNeedsShippingPatch(item, normalized, estimated)) return;
            chain = chain.then(function () {
                var properties = copyProperties(item.properties);
                properties[ATTRIBUTE_KEY] = normalized;
                properties[ESTIMATED_DATE_KEY] = estimated;
                patchedKeys.push(item.key);
                debugLog("patching line", item.key, {
                    preservedKeys: Object.keys(properties),
                });
                return changeLine({
                    id: item.key,
                    quantity: item.quantity,
                    properties: properties,
                }).catch(function (err) {
                    debugLog("patch failed", item.key, err);
                });
            });
        });

        return chain.then(function () {
            debugLog("patched line item keys", patchedKeys);
            return { ok: true, patchedKeys: patchedKeys };
        });
    }

    function cleanupMarkers(cart, shippingOption) {
        var normalized = normalizeOption(shippingOption);
        var matching = getMatchingMarkerLines(cart, normalized);
        var allMarkers = getMarkerLines(cart);
        var wrong = allMarkers.filter(function (item) {
            return matching.indexOf(item) === -1;
        });
        var actions = [];

        // Remove wrong marker SKUs by item key.
        wrong.forEach(function (item) {
            actions.push({ key: item.key, quantity: 0, reason: "wrong-marker" });
        });

        // Keep first matching line at qty 1; remove extras; reduce qty > 1.
        if (matching.length > 0) {
            var keep = matching[0];
            if (Number(keep.quantity) !== 1) {
                actions.push({
                    key: keep.key,
                    quantity: 1,
                    reason: "reduce-qty",
                });
            }
            for (var i = 1; i < matching.length; i++) {
                actions.push({
                    key: matching[i].key,
                    quantity: 0,
                    reason: "duplicate-matching",
                });
            }
        }

        var chain = Promise.resolve();
        actions.forEach(function (action) {
            chain = chain.then(function () {
                debugLog("marker cleanup", action);
                return changeLine({
                    id: action.key,
                    quantity: action.quantity,
                }).catch(function (err) {
                    debugLog("marker cleanup failed", action.key, err);
                });
            });
        });

        return chain.then(function () {
            return { ok: true, actions: actions };
        });
    }

    function syncMarker(shippingValue) {
        var KS = getKS();
        if (KS && typeof KS.syncShippingMarker === "function") {
            return KS.syncShippingMarker(shippingValue).then(function (result) {
                debugLog("marker sync result", result);
                return result;
            });
        }
        return Promise.resolve({ ok: false, reason: "no-syncShippingMarker" });
    }

    function syncAttributes(shippingValue) {
        var KS = getKS();
        if (KS && typeof KS.syncCartAttribute === "function") {
            return KS.syncCartAttribute(shippingValue);
        }
        return Promise.resolve(null);
    }

    function refreshCartUI() {
        var cartSidebar = document.querySelector("cart-sidebar");
        if (cartSidebar && typeof cartSidebar.reload === "function") {
            return Promise.resolve()
                .then(function () {
                    return cartSidebar.reload();
                })
                .catch(function () {});
        }

        // Main cart page: soft reload if we are on /cart.
        try {
            var path = window.location.pathname || "";
            var cartPath = getCartUrl().replace(window.location.origin, "");
            if (
                path === cartPath ||
                path === "/cart" ||
                /\/cart\/?$/.test(path)
            ) {
                // Prefer section re-render hooks if present; otherwise leave page.
                var mainCart = document.querySelector("cart-items, #main-cart-items, .cart");
                if (!mainCart) {
                    /* no-op */
                }
            }
        } catch (_) {
            /* ignore */
        }
        return Promise.resolve();
    }

    function showSuccessMessage() {
        var existing = document.getElementById(SUCCESS_TOAST_ID);
        if (existing) existing.remove();

        var el = document.createElement("div");
        el.id = SUCCESS_TOAST_ID;
        el.className = "ks-shipping-checkout-toast";
        el.setAttribute("role", "status");
        el.textContent = SUCCESS_MESSAGE;

        var mount =
            document.querySelector(".cart-sidebar__footer-actions") ||
            document.querySelector(".cart__ctas") ||
            document.querySelector(".cart__footer") ||
            document.body;
        mount.appendChild(el);

        window.setTimeout(function () {
            try {
                el.classList.add("is-visible");
            } catch (_) {
                /* ignore */
            }
        }, 10);

        window.setTimeout(function () {
            try {
                el.classList.remove("is-visible");
                window.setTimeout(function () {
                    if (el.parentNode) el.parentNode.removeChild(el);
                }, 300);
            } catch (_) {
                /* ignore */
            }
        }, 6000);
    }

    function repairCartAfterSelection(shippingValue) {
        var normalized = normalizeOption(shippingValue);
        debugLog("selected shipping option", normalized);

        var run = function () {
            var KS = getKS();
            var sync =
                KS && typeof KS.syncCartShippingSelection === "function"
                    ? KS.syncCartShippingSelection(normalized)
                    : Promise.reject(
                          new Error(
                              "We couldn't update shipping for every item in your cart. Please try again."
                          )
                      );
            return sync.then(function (result) {
                var cart = result && result.cart;
                return refreshCartUI().then(function () {
                    showSuccessMessage();
                    syncProductDynamicCheckoutState();
                    syncCartExpressVisibility(cart);
                    return { ok: true, shippingValue: normalized };
                });
            });
        };

        _repairChain = _repairChain.then(run, run);
        return _repairChain;
    }

    function openGateAndRepair(trigger) {
        var Gate = getGate();
        if (!Gate || typeof Gate.openPicker !== "function") {
            debugLog("KSShippingGate.openPicker unavailable");
            return Promise.resolve(null);
        }

        return Gate.openPicker({ trigger: trigger || null }).then(function (
            result
        ) {
            if (!result || !result.shippingValue) {
                debugLog("gate cancelled or empty");
                return null;
            }
            // persistSelection already ran inside gate confirm; still run
            // full repair for line patches + marker cleanup.
            return repairCartAfterSelection(result.shippingValue);
        });
    }

    function blockEvent(event) {
        if (!event) return;
        try {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
        } catch (_) {
            /* ignore */
        }
    }

    function isCheckoutSubmit(event) {
        var form = event && event.target;
        if (!form || form.tagName !== "FORM") return false;

        // Never touch BYOB builder forms.
        if (form.matches && form.matches("[data-ks-byob-form]")) return false;
        if (form.closest && form.closest("[data-ks-byob-root], .ks-byob")) {
            return false;
        }

        var submitter = event.submitter;
        if (submitter) {
            return submitter.name === "checkout";
        }

        if (!(form.querySelector && form.querySelector('[name="checkout"]'))) {
            return false;
        }

        var action = String(form.getAttribute("action") || "").toLowerCase();
        return (
            form.id === "cart" ||
            action.indexOf("/cart") !== -1 ||
            action.indexOf("checkout") !== -1
        );
    }

    function isCheckoutClickTarget(target) {
        if (!target || !target.closest) return null;

        // Never intercept BYOB add-to-cart.
        if (target.closest("[data-ks-byob-form], [data-ks-byob-root], .ks-byob")) {
            var byobAdd = target.closest(
                "[data-ks-byob-add], [data-ks-byob-submit], button"
            );
            if (byobAdd && byobAdd.name !== "checkout") return null;
        }

        var checkoutBtn = target.closest('[name="checkout"]');
        if (checkoutBtn) return checkoutBtn;

        var checkoutLink = target.closest('a[href*="/checkout"]');
        if (checkoutLink) return checkoutLink;

        // Best-effort cart accelerated checkout (light DOM only).
        var cartAccel = target.closest(
            ".cart__dynamic-checkout-buttons, .additional-checkout-buttons, shopify-accelerated-checkout-cart, shopify-accelerated-checkout"
        );
        if (cartAccel) {
            // Ignore product-form payment buttons here; those are handled separately.
            if (target.closest("product-form, .product-form")) return null;
            return cartAccel;
        }

        return null;
    }

    function validateBeforeCheckout(event) {
        if (_busy) {
            blockEvent(event);
            return Promise.resolve({ allowed: false, reason: "busy" });
        }

        if (event && event.target && event.target.getAttribute) {
            if (event.target.getAttribute(VALIDATED_ATTR) === "1") {
                event.target.removeAttribute(VALIDATED_ATTR);
                debugLog("allowing previously validated checkout");
                return Promise.resolve({ allowed: true, skipped: true });
            }
        }

        debugLog("checkout attempt caught", event && event.type);

        blockEvent(event);
        _busy = true;

        return fetchCartJson()
            .then(function (cart) {
                var validity = getCartValidity(cart);
                debugLog("cart validity result", validity);
                if (validity.missingShippingOption) {
                    debugLog("missing shipping option");
                }
                if (validity.missingEstimatedShipDate) {
                    debugLog("missing estimated ship date");
                }
                if (validity.missingMarkerSku) {
                    debugLog("missing marker SKU");
                }
                if (validity.missingCartNote) {
                    debugLog("missing cart note");
                }

                syncCartExpressVisibility(cart);

                if (!validity.requiresShipping || validity.valid) {
                    _busy = false;
                    // Re-dispatch / continue original action.
                    resumeCheckout(event);
                    return { allowed: true, validity: validity };
                }

                var trigger =
                    (event && event.submitter) ||
                    (event && event.target) ||
                    document.activeElement;

                return openGateAndRepair(trigger).then(function (repair) {
                    _busy = false;
                    return {
                        allowed: false,
                        repaired: Boolean(repair && repair.ok),
                        validity: validity,
                    };
                });
            })
            .catch(function (err) {
                _busy = false;
                debugLog("validateBeforeCheckout error", err);
                // Fail closed for seafood carts would be ideal, but if cart.js
                // fails we block and surface nothing noisy in production.
                return { allowed: false, error: String(err) };
            });
    }

    function resumeCheckout(event) {
        if (!event) return;

        if (event.type === "submit" && event.target) {
            var form = event.target;
            form.setAttribute(VALIDATED_ATTR, "1");
            var submitter =
                event.submitter ||
                form.querySelector('[name="checkout"]');
            try {
                if (typeof form.requestSubmit === "function" && submitter) {
                    form.requestSubmit(submitter);
                } else if (typeof form.requestSubmit === "function") {
                    form.requestSubmit();
                } else {
                    HTMLFormElement.prototype.submit.call(form);
                }
            } catch (_) {
                try {
                    HTMLFormElement.prototype.submit.call(form);
                } catch (__) {
                    /* ignore */
                }
            }
            return;
        }

        if (event.type === "click") {
            var target = isCheckoutClickTarget(event.target) || event.target;
            if (target && target.tagName === "A" && target.href) {
                window.location.href = target.href;
                return;
            }
            if (target && target.name === "checkout") {
                var formEl =
                    target.form ||
                    (target.getAttribute &&
                        target.getAttribute("form") &&
                        document.getElementById(target.getAttribute("form")));
                if (formEl) {
                    formEl.setAttribute(VALIDATED_ATTR, "1");
                    try {
                        if (typeof formEl.requestSubmit === "function") {
                            formEl.requestSubmit(target);
                        } else {
                            HTMLFormElement.prototype.submit.call(formEl);
                        }
                    } catch (_) {
                        /* ignore */
                    }
                    return;
                }
            }

            // Best-effort: accelerated checkout may live in shadow/iframe.
            // After a valid cart check, send the customer to checkout.
            try {
                window.location.href = getCheckoutUrl();
            } catch (_) {
                /* ignore */
            }
        }
    }

    function onSubmitCapture(event) {
        if (!isCheckoutSubmit(event)) return;
        if (event.target && event.target.getAttribute(VALIDATED_ATTR) === "1") {
            event.target.removeAttribute(VALIDATED_ATTR);
            return;
        }
        validateBeforeCheckout(event);
    }

    function findProductFormHost(node) {
        if (!node || !node.closest) return null;
        return node.closest("product-form");
    }

    function getProductFormElement(host) {
        if (!host) return null;
        return (
            host.querySelector('form[data-type="add-to-cart-form"]') ||
            host.querySelector("form") ||
            (host.tagName === "FORM" ? host : null)
        );
    }

    function upsertHiddenProperty(form, name, value) {
        if (!form || !name) return null;
        var input = form.querySelector(
            'input[type="hidden"][name="' + name + '"]'
        );
        if (!input) {
            input = document.createElement("input");
            input.type = "hidden";
            input.name = name;
            form.appendChild(input);
        }
        input.value = value || "";
        return input;
    }

    /**
     * Ensure the product form carries Shipping Option + Estimated Ship Date
     * so Shop Pay / Buy Now cannot launch without line properties.
     * Returns { ok, shippingValue, estimatedShipDate }.
     */
    function ensureFormShippingProperties(form) {
        var KS = getKS();
        if (!form) {
            return { ok: false, shippingValue: "", estimatedShipDate: "" };
        }

        var shippingValue = "";
        var estimatedShipDate = "";

        if (KS && typeof KS.getEffectiveOption === "function") {
            shippingValue = normalizeOption(KS.getEffectiveOption(form) || "");
        }
        if (!shippingValue && KS && typeof KS.getSelectedOption === "function") {
            shippingValue = normalizeOption(KS.getSelectedOption(form) || "");
        }
        if (!shippingValue && KS && typeof KS.getStoredOption === "function") {
            shippingValue = normalizeOption(KS.getStoredOption() || "");
        }

        if (!isValidOption(shippingValue)) {
            return { ok: false, shippingValue: "", estimatedShipDate: "" };
        }

        estimatedShipDate = getEstimatedForOption(shippingValue);

        var select = form.querySelector("[data-ks-shipping-select]");
        if (select) {
            select.value = shippingValue;
        } else {
            upsertHiddenProperty(
                form,
                "properties[Shipping Option]",
                shippingValue
            );
        }

        var dateInput =
            form.querySelector("[data-ks-estimated-ship-date-input]") ||
            form.querySelector(
                'input[name="properties[Estimated Ship Date]"]'
            );
        if (dateInput) {
            dateInput.value = estimatedShipDate;
        } else {
            upsertHiddenProperty(
                form,
                "properties[Estimated Ship Date]",
                estimatedShipDate
            );
        }

        // Forms without an inline select also need a Shipping Option field.
        if (!select) {
            upsertHiddenProperty(
                form,
                "properties[Shipping Option]",
                shippingValue
            );
        }

        return {
            ok: Boolean(shippingValue && estimatedShipDate),
            shippingValue: shippingValue,
            estimatedShipDate: estimatedShipDate,
        };
    }

    function formHasShippingProperties(form) {
        if (!form) return false;
        var select = form.querySelector("[data-ks-shipping-select]");
        var optionValue = "";
        if (select) {
            optionValue = normalizeOption(select.value || "");
        } else {
            var optionInput = form.querySelector(
                'input[name="properties[Shipping Option]"]'
            );
            optionValue = normalizeOption(
                (optionInput && optionInput.value) || ""
            );
        }
        if (!isValidOption(optionValue)) return false;

        var dateInput =
            form.querySelector("[data-ks-estimated-ship-date-input]") ||
            form.querySelector(
                'input[name="properties[Estimated Ship Date]"]'
            );
        var dateValue = dateInput ? String(dateInput.value || "").trim() : "";
        return Boolean(dateValue);
    }

    function isCardOrQuickAddSurface(host) {
        if (!host) return false;
        if (host.closest && host.closest(".ks-grid-card-quick-add, .grid-item, squama-item, .card-wrapper, .product-card")) {
            // Main product page forms live in product info, not grid cards.
            if (host.closest(".product__info-container, .product__info-wrapper, [id^=\"MainProduct\"], .product-main")) {
                return false;
            }
            return true;
        }
        if (host.classList && host.classList.contains("sr-only")) return true;
        return false;
    }

    function handleProductExpressClick(event, productForm) {
        if (!productFormRequiresShipping(productForm)) return false;

        // Shipping-required products must never use product-level Shop Pay /
        // Buy Now — that path skips cart note + marker for ShipStation.
        blockEvent(event);
        debugLog("blocked product-level express for shipping-required product");
        return true;
    }

    function onClickCapture(event) {
        var productForm = findProductFormHost(event.target);
        if (productForm) {
            var paymentBtn =
                event.target.closest &&
                event.target.closest(PRODUCT_EXPRESS_SELECTOR);
            if (paymentBtn && productForm.contains(paymentBtn)) {
                handleProductExpressClick(event, productForm);
                return;
            }
        }

        var hit = isCheckoutClickTarget(event.target);
        if (!hit) return;

        // Cart checkout / link / best-effort accelerated (not product-form).
        if (hit.closest && hit.closest("product-form, .product-form")) {
            return;
        }

        validateBeforeCheckout(event);
    }

    function productFormRequiresShipping(host) {
        if (!host) return false;
        if (host.dataset && host.dataset.ksRequiresShipping === "false") {
            return false;
        }
        if (host.dataset && host.dataset.ksRequiresShipping === "true") {
            return true;
        }
        var Gate = getGate();
        var form = getProductFormElement(host);
        if (Gate && form && typeof Gate.formRequiresShipping === "function") {
            return Gate.formRequiresShipping(form, host);
        }
        return Boolean(form);
    }

    function syncProductDynamicCheckoutState() {
        document.querySelectorAll("product-form").forEach(function (host) {
            if (!productFormRequiresShipping(host)) {
                host.removeAttribute(PENDING_ATTR);
                host.removeAttribute(HIDE_EXPRESS_ATTR);
                return;
            }

            // Fail-safe: hide product-level express if theme setting still renders it.
            // Theme editor "Show dynamic checkout buttons" is the primary control.
            // Product express skips cart note + marker; customers use ATC → checkout.
            host.setAttribute(HIDE_EXPRESS_ATTR, "");
            host.setAttribute(PENDING_ATTR, "");
        });
    }

    /**
     * Cart-level express (Shop Pay etc.) may stay visible only when the cart
     * is valid. Does not remove Shop Pay from Shopify checkout.
     */
    function syncCartExpressVisibility(cart) {
        var apply = function (c) {
            var validity = getCartValidity(c || { items: [], attributes: {} });
            var block =
                validity.requiresShipping && !validity.valid;
            if (block) {
                document.documentElement.setAttribute(
                    CART_EXPRESS_BLOCKED_ATTR,
                    ""
                );
            } else {
                document.documentElement.removeAttribute(
                    CART_EXPRESS_BLOCKED_ATTR
                );
            }
            debugLog("cart express visibility", {
                blocked: block,
                valid: validity.valid,
                requiresShipping: validity.requiresShipping,
            });
            return validity;
        };

        if (cart) {
            apply(cart);
            return Promise.resolve(cart);
        }

        return fetchCartJson()
            .then(function (c) {
                apply(c);
                return c;
            })
            .catch(function (err) {
                debugLog("cart express sync failed", err);
                // Fail closed: hide cart express until we can validate.
                document.documentElement.setAttribute(
                    CART_EXPRESS_BLOCKED_ATTR,
                    ""
                );
            });
    }

    function bind() {
        if (document.documentElement.getAttribute(BOUND_ATTR) === "true") {
            return;
        }
        document.documentElement.setAttribute(BOUND_ATTR, "true");

        document.addEventListener("submit", onSubmitCapture, true);
        document.addEventListener("click", onClickCapture, true);

        window.addEventListener("ks-shipping-option-updated", function () {
            syncProductDynamicCheckoutState();
            syncCartExpressVisibility();
        });

        document.addEventListener("shopify:section:load", function () {
            syncProductDynamicCheckoutState();
            syncCartExpressVisibility();
        });

        // Refresh express gating when the cart drawer/page updates.
        document.addEventListener("cart:updated", function () {
            syncCartExpressVisibility();
        });

        try {
            if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS === "object") {
                subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
                    syncCartExpressVisibility();
                });
                subscribe(PUB_SUB_EVENTS.cartUpdateRequested, function () {
                    syncCartExpressVisibility();
                });
            }
        } catch (_) {
            /* ignore */
        }

        // Keep product express in sync when the inline shipping select changes.
        document.addEventListener(
            "change",
            function (event) {
                var target = event.target;
                if (
                    !target ||
                    !target.matches ||
                    !target.matches("[data-ks-shipping-select]")
                ) {
                    return;
                }
                syncProductDynamicCheckoutState();
            },
            true
        );

        syncProductDynamicCheckoutState();
        syncCartExpressVisibility();
        debugLog("bound");
    }

    window.KSShippingCheckoutGuard = {
        validateBeforeCheckout: validateBeforeCheckout,
        cartRequiresShipping: cartRequiresShipping,
        isCartValid: isCartValid,
        getCartValidity: getCartValidity,
        patchCartLines: patchCartLines,
        repairCartAfterSelection: repairCartAfterSelection,
        cleanupMarkers: cleanupMarkers,
        syncProductDynamicCheckoutState: syncProductDynamicCheckoutState,
        syncCartExpressVisibility: syncCartExpressVisibility,
        ensureFormShippingProperties: ensureFormShippingProperties,
        getCartJsonUrl: getCartJsonUrl,
        getCartChangeUrl: getCartChangeUrl,
        getCartUrl: getCartUrl,
        getCheckoutUrl: getCheckoutUrl,
    };

    if (
        document.readyState === "complete" ||
        document.readyState === "interactive"
    ) {
        bind();
    } else {
        document.addEventListener("DOMContentLoaded", bind);
    }
})();
